import React from 'react'
import Layout from '@scentregroup/shared/hub-components/layout'
import { useGetHrefContext } from '@scentregroup/shared/context'
import { completelyCanonicalUrl } from '@scentregroup/shared/helpers/canonical-link'
import { WhatsHappening } from '@scentregroup/shared/pages/centre/whats-happening'
import { localeToCountry } from '@scentregroup/shared/helpers/local-to-country'
import { setCacheHeaders } from '@scentregroup/shared/helpers/set-cache-headers'
import {
  getCentreDetails,
  CentreDetails,
  buildContactLinks,
} from '@scentregroup/shared/helpers/centre'
import { useStoredCentre } from '@scentregroup/shared/hooks/use-stored-centre'
import {
  GetServerSidePropsContext,
  GetServerSidePropsResult,
} from '../../../types/next'
import { isGetServerSidePropsResultSpecial } from '../../../lib/next'
import { makeApolloClient } from '../../../lib/apolloClient'
import { propsOrNotFound } from '../../../lib/props-or-not-found'
import {
  SGPageTemplate,
  SGPageTemplateContentCollection,
} from '@scentregroup/shared/types/page-templates'
import { CFEvent, CFOffer } from '@scentregroup/shared/types/contentful'
import { getWhatsHappeningFilters } from '../../../lib/get-whats-happening-filters'
import { getCentrePageTemplate } from '../../../lib/page-templates/fetching'
import { getWhatsHappeningList } from '../../../lib/get-whats-happening-list'
import {
  getTopStripProps,
  ITopStripProps,
} from '@scentregroup/shared/hub-components/top-strip'
import { shouldRedirectForMismatchedRoute } from '@scentregroup/shared/lib/redirects'
import {
  CentreStaticData,
  fetchCentreStaticData,
} from '@scentregroup/shared/helpers/fetch-header-static-data'

export interface IWhatsHappeningPageProps {
  country: ReturnType<typeof localeToCountry>
  topStripProps: ITopStripProps | null
  centre: CentreDetails
  template: SGPageTemplate | null
  headerStaticData: CentreStaticData
  events: CFEvent[]
  offers: CFOffer[]
  experienceFilters: string[]
  topicFilters: string[]
}

export const getServerSideProps = async (
  ctx: GetServerSidePropsContext<{
    centre: string
    id: string
    slug: string
  }>
): Promise<GetServerSidePropsResult<IWhatsHappeningPageProps>> => {
  setCacheHeaders(ctx.res)

  const centreSlug = ctx.params?.centre || ''
  const country = localeToCountry(ctx.locale)

  const apollo = makeApolloClient()
  const centre = await getCentreDetails(apollo, { slug: centreSlug, country })
  if (!centre) {
    return {
      notFound: true,
    }
  }
  const shouldRedirect = shouldRedirectForMismatchedRoute(
    '/[centre]/whats-happening',
    ctx.params,
    {
      centre: centre.slug,
    }
  )
  if (shouldRedirect) {
    return shouldRedirect
  }
  const [headerStaticData, template, topStripProps] = await Promise.all([
    fetchCentreStaticData(apollo, {
      country,
      centre,
    }),
    getCentrePageTemplate(apollo, centreSlug, 'whats-happening'),
    getTopStripProps(apollo, country, centreSlug),
  ])
  if (!headerStaticData || !template) {
    return {
      notFound: true,
    }
  }

  const result = propsOrNotFound({
    props: {
      headerStaticData,
      centre,
    },
  })

  if (isGetServerSidePropsResultSpecial(result)) {
    return result
  }
  const contactLinks = buildContactLinks(centre)

  const [whatsHappeningFilters, whatsHappeningListOrNull] = await Promise.all([
    getWhatsHappeningFilters(apollo, { ...centre, contactLinks }),
    getWhatsHappeningList(apollo, { ...centre, contactLinks }),
  ])

  const whatsHappeningList = whatsHappeningListOrNull ?? []
  const events = whatsHappeningList.filter(
    item => item?.__typename === 'CFEvent'
  ) as unknown as CFEvent[]
  const offers = whatsHappeningList.filter(
    item => item?.__typename === 'CFOffer'
  ) as unknown as CFOffer[]

  const experienceFilters: string[] =
    whatsHappeningFilters?.experienceFiltersCollection?.items?.map(
      f => f?.title || ''
    ) || []
  const topicFilters: string[] =
    whatsHappeningFilters?.topicFiltersCollection?.items?.map(
      f => f?.title || ''
    ) || []

  return {
    props: {
      topStripProps,
      headerStaticData,
      centre,
      country,
      events,
      offers,
      experienceFilters,
      topicFilters,
      template,
    },
  }
}

const WhatsHappeningPage: React.FC<
  React.PropsWithChildren<IWhatsHappeningPageProps>
> = props => {
  const {
    events,
    offers,
    country,
    centre,
    headerStaticData,
    experienceFilters,
    topicFilters,
    topStripProps,
    template,
  } = props
  const { title: centreName } = centre
  const metaData = {
    title: `What's Happening at Westfield ${centreName}`,
    description: `Westfield ${centreName} - Get the latest updates on what's happening in centre with news, events, offers & more. Check what's happening at Westfield ${centreName}!`,
  }
  const canonical = completelyCanonicalUrl(useGetHrefContext()())
  const contactLinks = buildContactLinks(centre)
  useStoredCentre(centre)

  return (
    <Layout
      country={country}
      topStrip={topStripProps}
      centre={{ ...centre, contactLinks }}
      header={{
        variant: 'centre',
        data: headerStaticData,
      }}
      metaData={{
        title: metaData.title,
        description: metaData.description,
        canonical,
      }}
    >
      <WhatsHappening
        events={events}
        offers={offers}
        centre={centre}
        topicFilters={topicFilters}
        experienceFilters={experienceFilters}
        template={template}
        modules={v2TemplateToV1FeatureModules(template) || []}
      />
    </Layout>
  )
}

export default WhatsHappeningPage

// This is a lousy return type but it's members are only consumed
// by legacy vanilla JS code anyway.
type V1FeatureModules = Record<string, unknown>[] | undefined

function v2TemplateToV1FeatureModules(
  v2Template: SGPageTemplate | null
): V1FeatureModules {
  if (!v2Template) {
    return undefined
  }

  const { modules } = v2Template
  return (
    modules.filter(
      ({ __typename }) => __typename === 'SGPageTemplateContentCollection'
    ) as SGPageTemplateContentCollection[]
  ).map(templateModule => {
    const { items, ...props } = templateModule
    return {
      contentTypes: null,
      relatedContent: null,
      displayMode: 'Featured Content',
      entries: items.map(({ image, callToAction, title, ...props }) => ({
        title: undefined,
        image: image.url,
        link: callToAction.url,
        body: title,
        ...props,
      })),
      sys: {
        contentTypeId: 'manualCollection',
      },
      ...props,
    }
  })
}
